import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiPlus, FiEdit, FiTrash2, FiX, FiSettings } from 'react-icons/fi';
import { showNotification } from './Notification';
import ConfirmDialog from './ConfirmDialog';
import ProgressBar from './ProgressBar';
import './BatchManagement.css';

const BatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [operationProgress, setOperationProgress] = useState({ type: null, progress: 0, message: '' });
  const [deletingId, setDeletingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/batches');
      setBatches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching batches:', error);
      showNotification('Error fetching batches', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBatch(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  const handleEdit = (batch) => {
    setEditingBatch(batch);
    setFormData({ name: batch.name, description: batch.description || '' });
    setShowModal(true);
  };

  const handleDelete = (batch) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Batch',
      message: `Are you sure you want to delete "${batch.name}"? This will remove the batch assignment from all trainees in this batch.`,
      onConfirm: async () => {
        setDeletingId(batch.id);
        setOperationProgress({ type: 'delete', progress: 0, message: 'Deleting batch...' });
        
        try {
          setOperationProgress(prev => ({ ...prev, progress: 50, message: 'Removing batch...' }));
          await axios.delete(`/api/batches/${batch.id}`);
          
          setOperationProgress(prev => ({ ...prev, progress: 100, message: 'Batch deleted successfully!' }));
          setTimeout(() => {
            setOperationProgress({ type: null, progress: 0, message: '' });
            showNotification('Batch deleted successfully', 'success');
            fetchBatches();
          }, 500);
        } catch (error) {
          setOperationProgress({ type: null, progress: 0, message: '' });
          const errorMessage = error.response?.data?.error || error.message || 'Error deleting batch';
          showNotification(errorMessage, 'error');
        } finally {
          setDeletingId(null);
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showNotification('Batch name is required', 'error');
      return;
    }

    setSaving(true);
    setOperationProgress({ 
      type: editingBatch ? 'update' : 'create', 
      progress: 0, 
      message: editingBatch ? 'Updating batch...' : 'Creating batch...' 
    });

    try {
      setOperationProgress(prev => ({ ...prev, progress: 30, message: 'Saving...' }));
      
      if (editingBatch) {
        await axios.put(`/api/batches/${editingBatch.id}`, formData);
        setOperationProgress(prev => ({ ...prev, progress: 100, message: 'Batch updated successfully!' }));
        showNotification('Batch updated successfully', 'success');
      } else {
        await axios.post('/api/batches', formData);
        setOperationProgress(prev => ({ ...prev, progress: 100, message: 'Batch created successfully!' }));
        showNotification('Batch created successfully', 'success');
      }

      setTimeout(() => {
        setOperationProgress({ type: null, progress: 0, message: '' });
        setShowModal(false);
        setEditingBatch(null);
        setFormData({ name: '', description: '' });
        fetchBatches();
      }, 500);
    } catch (error) {
      setOperationProgress({ type: null, progress: 0, message: '' });
      const errorMessage = error.response?.data?.error || error.message || 'Error saving batch';
      showNotification(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading batches...</div>;
  }

  return (
    <div className="batch-management">
      {operationProgress.type && (
        <div className="operation-progress-overlay">
          <div className="operation-progress-card">
            <ProgressBar 
              progress={operationProgress.progress} 
              message={operationProgress.message}
            />
          </div>
        </div>
      )}

      <div className="batch-header">
        <h1>
          <FiSettings /> Batch Management
        </h1>
        <button className="btn btn-primary" onClick={handleAdd}>
          <FiPlus /> Add Batch
        </button>
      </div>

      <div className="batches-grid">
        {Array.isArray(batches) && batches.map(batch => (
          <div key={batch.id} className="batch-card">
            <div className="batch-card-header">
              <h3>{batch.name}</h3>
              <div className="batch-actions">
                <button 
                  className="btn-icon" 
                  onClick={() => handleEdit(batch)}
                  disabled={deletingId === batch.id || saving}
                  title="Edit"
                >
                  <FiEdit />
                </button>
                <button 
                  className="btn-icon danger" 
                  onClick={() => handleDelete(batch)}
                  disabled={deletingId === batch.id || saving}
                  title="Delete"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
            {batch.description && (
              <p className="batch-description">{batch.description}</p>
            )}
            <div className="batch-meta">
              <span>Created: {new Date(batch.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal" onClick={() => { setShowModal(false); setEditingBatch(null); setFormData({ name: '', description: '' }); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingBatch ? 'Edit Batch' : 'Add Batch'}</h2>
              <button className="close-btn" onClick={() => { setShowModal(false); setEditingBatch(null); setFormData({ name: '', description: '' }); }}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="batch-form">
              <div className="form-group">
                <label>Batch Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., TVET BATCH A"
                  required
                  disabled={saving}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows="3"
                  disabled={saving}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditingBatch(null); setFormData({ name: '', description: '' }); }} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingBatch ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
      />
    </div>
  );
};

export default BatchManagement;

